import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'routes';
import moment from 'moment';

import { connect } from 'react-redux';
import * as fromApp from '../../../store/app';

import HomeReservationView from './HomeReservation.view';

import { TimeFormats } from 'app/constants/TimeFormat';
import { LocalStorage, SessionStorage } from 'app/constants/StorageConstants';
import { Clients } from 'app/enums/clients';
import { OrderType } from 'app/enums/order-types';
import { IServiceStore } from 'app/pages/Order/Order.model';
import { ByLineForm } from 'app/components/ReservationBar/ByLine/ByLine.model';
import { DestinationForm } from 'app/components/ReservationBar/ByDestination/ByDestination.model';
import { IShuttle } from 'app/models/shuttle';
import { HeaderType, UIState } from './HomeReservation.model';
import { ISupplier } from 'app/models/supplier';
import { RoutingType } from 'app/enums/shuttleRoutingType';

interface PropsFromState {
  client: Clients | null;
  titleShadow: boolean;
  titleColor: 'black' | 'white';
  supplier: ISupplier | null;
}

interface Props extends PropsFromState { }

const initialState: UIState = {
  headerType: HeaderType.Big,
};

const HomeReservation: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const navigate = useNavigate();

  const [uiState, setUiState] = useState<UIState>(initialState);

  //** */ UI Changes per supplier
  useEffect(() => {
    const client = localStorage.getItem(LocalStorage.Client);
    const supplier = props.supplier?.name;

    if (client === Clients.Taxelco) {
      switch (supplier) {
        case 'EXO':
          setUiState((prev: UIState) => {
            return {
              ...prev,
              headerType: HeaderType.Small,
            };
          });
          break;
        case 'STL':
          setUiState((prev: UIState) => {
            return {
              ...prev,
              headerType: HeaderType.Small,
            };
          });
      }
    }
  }, [localStorage, props.supplier?.name, setUiState]);
  //** */

  const onSubmitDestination = useCallback(
    (values: DestinationForm, orderType: OrderType | null) => {
      const searchBar = {
        orderType: orderType,
        from: values.from,
        to: values.to,
        date: values.date?.unix(),
        returnDate: values.returnDate?.unix(),
        pax: values.pax,
      };

      sessionStorage.setItem(SessionStorage.Filters, JSON.stringify(searchBar));
      navigate(Routes.Services);
    },
    [navigate],
  );

  const onSubmitLine = useCallback(
    (values: ByLineForm, shuttleType: 'simple' | 'fixable', shuttle: IShuttle) => {
      if (values.pickUp && values.dropOff) {
        const time = values.pickUpTime?.time;

        const date = values.pickUp.date?.clone();

        if (shuttle.times[values.pickUpTime!.timeIndex].overHours) {
          date?.add(1, 'day');
        }

        const serviceStore: IServiceStore = {
          from: {
            name: values.pickUp.addr,
            lat: values.pickUp.lat,
            lng: values.pickUp.lon,
          },
          to: {
            name: values.dropOff.addr,
            lat: values.dropOff.lat,
            lng: values.dropOff.lon,
          },
          date: date?.set({ hour: time?.hours(), minute: time?.minutes() }).unix() || 0,
          pickUpHour:
            shuttleType === 'simple' ? date?.set({ hour: time?.hours(), minute: time?.minutes() }).unix() || 0 : null,
          dropOffHour: moment(`${moment(date).format(TimeFormats.ServerFormatDate)} ${values.dropOffTime}`, TimeFormats.ServerFormatDateTime).unix() || 0,
          maxPassengers: 0,
          passengers: values.passengers,
          code: shuttle?.code,
          area: shuttle.area,
          line: shuttle.line,
          direction: shuttle.direction,
          name: shuttle?.name,
          orderType: OrderType.Shuttle,
          paymentTypes: shuttle?.paymentTypes,
          unit: shuttle.unit,
          profile: shuttle.profile,
          profileName: shuttle.profileName,
          profileCode: shuttle.profileCode,
          shuttleDeparture: typeof values.pickUpTime?.timeIndex === 'number' ? shuttle.times[values.pickUpTime.timeIndex].hour : null,
          amount: shuttle.amount,
        };

        if (shuttle.routingType === RoutingType.Exo && shuttle.times[0].type === 'arrival') {
          const stops = shuttle.times[values.initialTime!.timeIndex].stops;

          const time = values.initialTime!.time.clone();

          if (stops[stops.length - 1].alone) {
            time.subtract({ seconds: (stops[stops.length - 1].alone || 0) * 1.5 });
          }

          serviceStore.shuttleDeparture = time.format(TimeFormats.Time24);
        }

        sessionStorage.setItem(SessionStorage.Service, JSON.stringify(serviceStore));

        navigate(Routes.Book);
      }
    },
    [navigate],
  );

  const onOpenLink = () => {
    window.open('https://5900655.hs-sites.com/sensibilisation-%C3%A0-la-r%C3%A9servation-de-course', '_blank');
  };

  return (
    <HomeReservationView
      supplier={props.supplier?.name || null}
      uiState={uiState}
      client={props.client}
      titleShadow={props.titleShadow}
      titleColor={props.titleColor}
      onOpenLink={onOpenLink}
      onSubmitLine={onSubmitLine}
      onSubmitDestination={onSubmitDestination}
    ></HomeReservationView>
  );
};

HomeReservation.displayName = 'HomeReservation';
HomeReservation.defaultProps = {};

const mapStateToProps = (state: fromApp.AppState): PropsFromState => {
  return {
    client: state.settings.specs?.client || null,
    titleShadow: state.settings.specs?.pages.home.titleShadow || false,
    titleColor: state.settings.specs?.pages.home.titleColor || 'white',
    supplier: state.settings.supplierDetails,
  };
};

export default connect(mapStateToProps)(HomeReservation);
